import React, { useState } from "react";
import "./ManageCodeModal.css";
import Select from "../../../../components/reusable-select/Select";
import Switch from "../../../../components/reusable-switch/Switch";
import Button from "../../../../components/reusable-button/Button";
import InputBox from "../../../../components/reusable-input/InputBox";

interface Option {
  id: string;
  name: string;
}

export interface CodeFormData {
  category: Option | null;
  codeType: Option | null;
  code: string;
  usages: number;
  thresholdAlert: number;
  maxAllowed: number;
  expiryDate: string;
  startingDate: string;
  isActive: boolean;
}

interface ManageCodeModalProps {
  isEdit: boolean;
  initialData: Partial<CodeFormData> | null;
  onSubmit: (data: CodeFormData) => void;
  onClose: () => void;
  codeTypes: Option[];
  categories: Option[];
  loading?: boolean;
}

const ManageCodeModal: React.FC<ManageCodeModalProps> = ({
  isEdit,
  initialData,
  onClose,
  codeTypes,
  categories,
  onSubmit,
  loading = false,
}) => {
  const [formData, setFormData] = useState<CodeFormData>({
    category: initialData?.category || null,
    codeType: initialData?.codeType || null,
    code: initialData?.code || "",
    usages: initialData?.usages ?? 0,
    thresholdAlert: initialData?.thresholdAlert ?? 0,
    maxAllowed: initialData?.maxAllowed ?? 0,
    expiryDate: initialData?.expiryDate || "",
    isActive: initialData?.isActive ?? true,
    startingDate: initialData?.startingDate || "",
  });

  const [errors, setErrors] = useState<{
    [key in keyof CodeFormData]?: string;
  }>({});

  const handleChange = (field: keyof CodeFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({ ...prev, [field]: "" })); // Clear error on change
  };

  const getMaxAllowedLabel = () => {
    if (formData.codeType?.name === "Learning Course")
      return "Maximum Number of Courses Allowed";
    if (formData.codeType?.name === "Practice Test")
      return "Maximum Number of Practice Tests Allowed";
    return "Maximum Number of Practice Tests Allowed";
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!formData.category) newErrors.category = "Category is required";
    if (!formData.codeType) newErrors.codeType = "Code type is required";
    if (!formData.code.trim()) newErrors.code = "Code is required";
    if (formData.usages <= 0)
      newErrors.usages = "Max Usages must be greater than 0";
    if (formData.maxAllowed <= 0)
      newErrors.maxAllowed = "Maximum allowed must be greater than 0";
    if (formData.thresholdAlert <= 0)
      newErrors.thresholdAlert = "Threshold must be greater than 0";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      ...formData,
      category: formData.category?.id,
      codeType: formData.codeType?.id,
    };

    onSubmit(formData);
  };

  const getErrorStyle = (field: keyof CodeFormData): React.CSSProperties =>
    errors[field] ? { border: "1px solid red" } : {};

  return (
    <div className="code-modal-overlay">
      <form className="code-modal" onSubmit={handleAddCode}>
        <h2 className="code-modal-title">Add Code</h2>

        <div className="code-modal-field">
          <label className="code-modal-label">
            Select Category <span>*</span>
          </label>
          <Select
            name="category"
            label=""
            options={categories.map((c) => ({ label: c.name, value: c.id }))}
            value={formData.category?.id || ""}
            onChange={(e) => {
              const selected =
                categories.find((c) => c.id === e.target.value) || null;
              handleChange("category", selected);
            }}
            style={getErrorStyle("category")}
          />
          {errors.category && <p className="error-text">{errors.category}</p>}
        </div>

        <div className="code-modal-field">
          <label className="code-modal-label">
            Select Code Type <span>*</span>
          </label>
          <Select
            name="codeType"
            label=""
            options={codeTypes.map((item) => ({
              label: item.name,
              value: item.id,
            }))}
            value={formData.codeType?.id || ""}
            onChange={(e) => {
              const selected =
                codeTypes.find((item) => item.id === e.target.value) || null;
              handleChange("codeType", selected);
            }}
            style={getErrorStyle("codeType")}
          />
          {errors.codeType && <p className="error-text">{errors.codeType}</p>}
        </div>

        {formData.codeType && (
          <>
            <div className="code-modal-field">
              <InputBox
                label="Code"
                value={formData.code}
                onChange={(e) => handleChange("code", e.target.value)}
                type="text"
                name="code"
                placeholder="Enter Code Here"
                style={getErrorStyle("code")}
                required
              />
              {errors.code && <p className="error-text">{errors.code}</p>}
            </div>

            <div className="code-modal-field">
              <InputBox
                label="Max Usages"
                value={formData.usages.toString()}
                onChange={(e) => handleChange("usages", Number(e.target.value))}
                type="number"
                name="usages"
                placeholder="Enter number of usages"
                style={getErrorStyle("usages")}
                required
              />
              {errors.usages && <p className="error-text">{errors.usages}</p>}
            </div>

            <div className="code-modal-field">
              <InputBox
                label={getMaxAllowedLabel()}
                value={formData.maxAllowed.toString()}
                onChange={(e) =>
                  handleChange("maxAllowed", Number(e.target.value))
                }
                type="number"
                name="maxAllowed"
                placeholder="Enter maximum allowed"
                style={getErrorStyle("maxAllowed")}
                required
              />
              {errors.maxAllowed && (
                <p className="error-text">{errors.maxAllowed}</p>
              )}
            </div>

            <div className="code-modal-field">
              <InputBox
                label="Threshold Alert"
                value={formData.thresholdAlert.toString()}
                onChange={(e) =>
                  handleChange("thresholdAlert", Number(e.target.value))
                }
                type="number"
                name="thresholdAlert"
                placeholder="Enter threshold"
                style={getErrorStyle("thresholdAlert")}
                required
              />
              {errors.thresholdAlert && (
                <p className="error-text">{errors.thresholdAlert}</p>
              )}
            </div>
          </>
        )}
        {isEdit && (
          <div className="code-modal-switch">
            <Switch
              label={formData.isActive ? "Active" : "Inactive"}
              checked={formData.isActive}
              onChange={() => handleChange("isActive", !formData.isActive)}
            />
          </div>
        )}

        <div className="code-modal-actions">
          <Button variant="outlined" label="Cancel" onClick={onClose} />
          <Button
            variant="solid"
            label={isEdit ? "Save Changes" : "Add Code"}
            loading={loading}
            type="submit"
          />
        </div>
      </form>
    </div>
  );
};

export default ManageCodeModal;
